import { X, Tag, Package2, Percent, ExternalLink } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useThemeSettings } from '../../hooks/useThemeSettings';
import { useMultilingualSettings } from '../../hooks/useMultilingualSettings';
import type { Product } from '../../hooks/useProductSystem';

interface ProductDetailsProps {
  product: Product;
  onClose: () => void;
}

// List of valid currency codes
const VALID_CURRENCIES = ['EUR', 'USD', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY'];

export function ProductDetails({ product, onClose }: ProductDetailsProps) {
  const { colors } = useDesignColors();
  const { theme } = useThemeSettings();
  const { settings: multilingualSettings } = useMultilingualSettings();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const isDark = theme.mode === 'dark';

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get translations for the current language
  const currentLang = multilingualSettings.currentLanguage || 'fr';
  const translations = multilingualSettings.translations[currentLang];
  const productTranslation = translations?.miniApps?.find(app => app.id === 'products')?.content?.productDetails?.[product.id];

  // Get translated product data
  const translatedProduct = {
    ...product,
    name: productTranslation?.name || product.name,
    description: productTranslation?.description || product.description,
    category: productTranslation?.category || product.category,
    tags: productTranslation?.tags || product.tags
  };

  // Get all product images
  const allImages = [
    product.image_url,
    ...(product.metadata?.additional_images || [])
  ].filter(Boolean) as string[];

  const formatPrice = (price: number, currency?: string) => {
    try {
      // Ensure currency is a valid ISO 4217 currency code
      const validCurrency = VALID_CURRENCIES.includes(currency?.toUpperCase() || '') 
        ? currency?.toUpperCase() 
        : 'EUR';
      
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: validCurrency,
        maximumFractionDigits: 0
      }).format(price);
    } catch (err) {
      // Fallback formatting if currency formatting fails
      return `${price} EUR`;
    }
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`w-full ${isMobile ? 'h-full' : 'max-w-3xl h-[80vh]'} flex flex-col overflow-hidden rounded-xl border ${
        isDark ? 'bg-gray-900/95 border-white/10' : 'bg-white border-gray-200'
      }`}>
        <div className={`flex items-center justify-between p-6 border-b ${
          isDark ? 'border-white/10' : 'border-gray-200'
        }`}>
          <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {translatedProduct.name}
          </h3>
          <button
            onClick={onClose}
            className={`text-gray-400 hover:text-${isDark ? 'white' : 'black'} transition-colors`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-8`}>
              {/* Images */}
              <div className={`${isMobile ? 'w-full' : 'w-1/2'} space-y-4`}>
                {allImages.length > 0 && (
                  <div className="relative">
                    <div className="aspect-square rounded-xl overflow-hidden bg-black/20">
                      <img
                        src={allImages[currentImageIndex]}
                        alt={translatedProduct.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {allImages.length > 1 && (
                      <>
                        <button
                          onClick={handlePrevImage}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={handleNextImage}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                )}
                {allImages.length > 1 && (
                  <div className="grid grid-cols-4 gap-2">
                    {allImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`aspect-square rounded-lg overflow-hidden ${
                          index === currentImageIndex ? 'ring-2 ring-indigo-500' : ''
                        }`}
                      >
                        <img
                          src={image}
                          alt={`${translatedProduct.name} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Informations */}
              <div className={`${isMobile ? 'w-full' : 'w-1/2'} space-y-6`}>
                <div>
                  {product.price && (
                    <div className="mb-4">
                      {product.discount ? (
                        <div className="flex flex-col">
                          <span className="text-2xl font-bold text-red-400">
                            {formatPrice(product.price * (1 - product.discount / 100), product.currency)}
                          </span>
                          <span className="text-lg line-through text-gray-500">
                            {formatPrice(product.price, product.currency)}
                          </span>
                          <span className="text-red-400 font-medium">(-{product.discount}%)</span>
                        </div>
                      ) : (
                        <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatPrice(product.price, product.currency)}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {product.category && (
                      <span 
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm"
                        style={{
                          background: `linear-gradient(135deg, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                          color: colors.gradientStart
                        }}
                      >
                        <Tag className="w-4 h-4" />
                        {translatedProduct.category}
                      </span>
                    )}
                    <span 
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm"
                      style={{
                        background: `linear-gradient(135deg, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                        color: colors.gradientStart
                      }}
                    >
                      <Package2 className="w-4 h-4" />
                      {translations?.stockLabel || 'Stock'}: {product.stock || 0}
                    </span>
                  </div>
                </div>

                {translatedProduct.description && (
                  <div>
                    <h4 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Description</h4>
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} whitespace-pre-wrap`}>{translatedProduct.description}</p>
                  </div>
                )}

                {product.tags && product.tags.length > 0 && (
                  <div>
                    <h4 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {translations?.tagsLabel || 'Tags'}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {product.tags.map((tag, index) => (
                        <span
                          key={index}
                          className={`px-3 py-1.5 rounded-lg text-sm ${
                            isDark 
                              ? 'bg-white/5 text-gray-300' 
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {translatedProduct.tags?.[index] || tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {product.sku && (
                  <div>
                    <h4 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>SKU</h4>
                    <p className={`${isDark ? 'bg-white/5 text-white' : 'bg-gray-100 text-gray-700'} rounded-lg px-3 py-2`}>
                      {product.sku}
                    </p>
                  </div>
                )}

                {product.metadata?.redirect_url && (
                  <a
                    href={product.metadata.redirect_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
                    style={{
                      background: `linear-gradient(135deg, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                      color: colors.gradientStart
                    }}
                  >
                    <ExternalLink className="w-4 h-4" />
                    {translations?.viewProductLabel || 'View product'}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}